<template>
  <section class="tw-pb-8">
    <div class="table__div tw-bg-white tw-rounded-lg tw-mb-10">
      <exportToCsv
        v-if="allEmployeeInvites && allEmployeeInvites.length > 0 && esopToken"
        class="tw-ml-auto tw-mr-8"
        :data="allEmployeeInvites"
        :fileName="esopToken.name"
        docType="Employees"
      />
      <table id="transaction-table">
        <thead v-if="tableRows.length !== 0" class="header">
          <tr class="header-row">
            <th
              class="header-data tw-font-normal tw-py-6"
              ref="tableHead"
              :style="gridStyling"
            >
              <div
                class="tw-justify-self-start tw-text-sm tw-text-black-light"
                :class="{
                  'tw-hidden': header === 'misc',
                  'tw-mx-auto': header === 'Action',
                }"
                v-for="(header, index) in tableHeaders"
                :key="'header' + index"
              >
                {{ header }}
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="table-body">
          <tr class="body-row">
            <td class="body-data" ref="tableRow">
              <div
                class="data tw-py-6 tw-border-gray-dark hover:tw-bg-gray-lightest tw-cursor-pointer"
                v-for="employee in tableRows"
                :key="'row' + employee.misc.split('/')[1]"
                :style="gridStyling"
                @click="viewUserInfo(employee)"
              >
                <div
                  v-for="(detail, key, index) in employee"
                  :key="index"
                  class="tw-self-center"
                  :class="{ 'tw-hidden': key.includes('misc') }"
                >
                  <!-- <ProgressBar
                    class="tw-w-28 tw-mx-auto"
                    :currentNumber="employee.status === 'Pending' ? 0 : detail"
                    :totalValue="1"
                    v-if="key === 'vested'"
                  /> -->
                  <p
                    :class="[
                      availableStatuses.includes(detail)
                        ? `${detail.toLowerCase()} tw-text-center`
                        : null,
                      key.includes('misc') ? 'tw-hidden' : null,
                    ]"
                    v-html="detail"
                    class="tw-text-sm tw-text-black"
                  ></p>
                </div>
                <button
                  class="tw-text-sm tw-text-primary hover:tw-bg-transparent hover:tw-text-primary"
                  @click="viewUserInfo(employee)"
                >
                  Details
                </button>
              </div>
              <div v-if="tableRows.length === 0" class="tw-py-8">
                <EmployeesEmptyState />
              </div>

              <!-- <EmptyState v-if="tableRows.length === 0">
                <template v-slot:message>
                  <p class="message tw-text-center tw-text-gray tw-my-3">
                    You have no employee with ESO/RSU.
                  </p>
                </template>
              </EmptyState> -->
            </td>
          </tr>
        </tbody>
        <VestingInfoModal
          :showVestingInfo="showVestingInfo"
          :employee="employee"
          @close="showVestingInfo = false"
        />
      </table>
    </div>
    <div class="tw-flex tw-justify-start tw-h-12" v-if="showPagination">
      <Pagination
        v-if="tableRows.length > 0"
        :totalPage="totalPage"
        :currentPage="currentPage"
        :maxVisible="9"
        @pageChange="gotoPage"
      />
    </div>
  </section>
</template>

<script>
  import Pagination from "@/components/navigation/Pagination";
  import { mapActions, mapGetters } from "vuex";
  import EmptyState from "@/components/notification/EmptyState";
  import SideModal from "@/components/general/sidemodal/SideModal";
  import ProgressBar from "@/components/general/ProgressBar";
  import VestingInfoForm from "@/components/employeesBenefit/forms/VestingInfoForm";
  import EmployeesEmptyState from "@/components/employeesBenefit/EmployeesEmptyState";
  import TopUpForm from "@/components/employeesBenefit/forms/TopUpForm";
  import VestingInfoModal from "@/components/employeesBenefit/VestingInfoModal.vue";
  import exportToCsv from "@/components/general/exportToCSV";
  export default {
    name: "EmployeesTable",

    components: {
      Pagination,
      SideModal,
      ProgressBar,
      EmptyState,
      VestingInfoForm,
      TopUpForm,
      EmployeesEmptyState,
      exportToCsv,
      VestingInfoModal,
    },

    props: {
      tableHeaders: { type: Array, default: () => [], required: true },
      tableRows: { type: Array, default: () => [], required: true },
      columnNumber: { type: Number, default: () => 0, required: true },
      columnWidth: { type: Number, default: () => 0, required: true },
      totalPage: { type: Number, default: () => 0, required: true },
      currentPage: { type: Number, default: () => 0, required: true },
      hasActions: {
        type: Boolean,
        required: false,
        default: () => false,
      },
      showPagination: {
        type: Boolean,
        required: false,
        default: () => true,
      },
    },

    emits: ["go-to-page", "reload-data"],

    data() {
      return {
        rows: [],
        loading: false,
        showVestingInfo: false,
        availableStatuses: [
          "Signed",
          "Accepted",
          "Pending",
          "Terminated",
          "Rejected",
          "ESOP",
          "RSU",
        ],
        employee: {},
        showActions: false,
        activeRow: "",
      };
    },
    computed: {
      ...mapGetters("Esop", [
        "esoOverview",
        "esopToken",
        "employeeInvite",
        "employeeInfo",
        "allEmployeeInvites",
      ]),
      gridStyling() {
        if (this.hasActions) {
          return {
            "grid-template-columns": `${150}px ${150}px ${120}px ${120}px ${120}px ${100}px repeat(${
              this.columnNumber
            }, ${this.columnWidth}px)`,
          };
        }
        return {
          "grid-template-columns": `${50}px ${150}px ${150}px ${150}px ${120}px ${120}px repeat(${
            this.columnNumber
          }, ${this.columnWidth}px)`,
        };
      },
      status() {
        let status = null;
        const defaultStatus = this.employee?.status;
        if (defaultStatus) {
          switch (defaultStatus) {
            case "Accepted":
              status = "Contract Signed";
              break;
            default:
              status = this.employee?.status;
              break;
          }
        }
        return status;
      },
      vestingSchedule() {
        // vesting calculation:
        // X = ratio of vested esop
        let vestedEsop = null;
        let vestedShares = this.employee.employee?.vested_shares;
        let allocatedShares = this.employee.employee?.allocated_shares;
        vestedEsop = vestedShares / allocatedShares;
        // Total vesting duration in days = D
        // const vestingDuration = this.employee.vesting_duration * 365;
        // Vesting frequency in days = C
        // const vestingFrequency = this.employee.vesting_frequency;
        // Number of vesting cycles elapsed = A
        // A = (currentData - startDate) in days / C
        // const daysElapsed = 1825;
        // const daysElapsed = Math.ceil(
        //   (new Date() -
        //     new Date(this.employee.start_date || this.employee.createdAt)) /
        //     (1000 * 3600 * 24)
        // );
        // const vestingElapsed = daysElapsed / vestingFrequency;
        // Total number of vesting cycles in duration = B
        // B = D / C
        // const vestingCycles = vestingDuration / vestingFrequency;
        // X = A/B
        // vestedEsop = vestingElapsed / vestingCycles;
        return vestedEsop;
      },
      vestingFrequency() {
        let frequency = null;
        switch (Number(this.employee.vesting_frequency)) {
          case 1:
            frequency = "Daily";
            break;
          case 30:
            frequency = "Monthly";
            break;
          case 91:
            frequency = "Quarterly";
          case 182:
            frequency = "Bi-annually";
          case 365:
            frequency = "Yearly";
            break;
        }
        return frequency;
      },
      marketPrice() {
        return this.esopToken.valuation / this.esopToken.total_supply;
      },
    },

    mounted() {
      this.getEsopToken();
      this.getAllEmployeeInvites({ page: 1 });
    },
    watch: {
      employeeInfo(val) {
        this.employee = val;
      },
    },
    methods: {
      ...mapActions(["showToast"]),
      ...mapActions("Esop", [
        "getEsopToken",
        "getEmployeeInvite",
        "getAllEmployeeInvites",
      ]),
      async viewUserInfo(user) {
        const id = user.misc.split("/")[1];
        try {
          await this.getEmployeeInvite(id);
          this.showVestingInfo = true;
        } catch (error) {
          return error;
        }
      },
      closeVestingInfo() {
        this.showVestingInfo = false;
      },
      // setAction(user, index) {
      //   this.activeRow = index;
      //   const id = user.misc.split("/")[1];
      //   this.employee = this.employeeInvite.filter(
      //     (employee) => employee._id === id
      //   )[0];
      //   this.showActions = true;
      // },
      // async terminateInvite() {
      //   const id = this.employee._id;
      //   try {
      //     const response = await this.cancelInvite(id);
      //     const { message } = response.data;
      //     this.showToast({
      //       description: message,
      //       display: true,
      //       type: "success",
      //     });
      //     this.$router.push({ name: "Employees" });
      //     this.$emit("reload-data");
      //   } catch (error) {
      //     return error;
      //   }
      // },

      gotoPage(page) {
        this.$emit("go-to-page", page);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .table__div {
    overflow-x: scroll;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  #transaction-table {
    width: 100%;
    overflow-x: scroll;
    @media (min-width: 1280px) {
      width: 100%;
    }
    @include fadeIn;
    .header {
      .header-row {
        border-bottom: 1px solid #e1e6f0;
        .header-data {
          display: grid;
          column-gap: 2rem;
          color: #323e67;
          font-size: 14px;
          letter-spacing: -0.12px;
          /* &:first-of-type { */
          padding-left: 30px;
          /* box-sizing: border-box; */
          /* } */
          @media (min-width: 1280px) {
            grid-auto-flow: row;
          }
        }
      }
    }
    .table-body {
      .body-row {
        border-bottom: 1px solid #e1e6f0;
        .body-data {
          .data {
            display: grid;
            column-gap: 2rem;
            font-size: 0.7rem;
            padding-left: 30px;
            border-bottom: 1px solid #e1e6f0;
            .body-button {
              border-radius: 50%;
              border: 0;
              &:hover {
                background-color: transparent;
                box-shadow: 2px 2px 7px 2px #f0f4fd;
              }
            }
          }
        }
      }
    }

    .action {
      &__div {
        background: #ffffff;
        box-shadow: 0px 2px 30px rgba(19, 38, 83, 0.06);
        border-radius: 4px;
        width: 200px;
        position: absolute;
        right: 50px;
        top: 30px;
        z-index: 2;
        height: max-content;
        box-sizing: border-box;
      }
      &__list {
        height: 56px;
        display: flex;
        cursor: pointer;
        align-items: center;
        width: 100%;
      }
    }
    // Added with javascript
    .signed,
    .pending,
    .rejected,
    .terminated,
    .esop,
    .rsu {
      text-align: center;
      padding: 0.5rem;
      border-radius: 5px;
    }
    .signed,
    .esop {
      color: $success;
      background-color: $success-light;
    }
    .pending {
      color: $warning;
      background-color: $warning-light;
    }
    .terminated,
    .rejected {
      color: $error;
      background-color: $error-light;
    }
    .rsu {
      color: $brand-primary;
      background-color: $brand-primary-light;
    }
  }
</style>
